import { SkyBadge, SkyButton, SkyCard, SkyText } from "@skypointcloud/skypoint-ui";
import type { ReactNode } from "react";
import { useLocation } from "wouter";
import type { TransformationGraph } from "@shared/types/transformations";
import { usePermissions } from "@/hooks/use-permissions";
import { absolutePath } from "@/lib/routes";
import {
  BUNDLE_DETAIL_WITHHELD,
  DBT_SETTINGS_PATH,
  OPEN_DBT_SETTINGS_LABEL,
  ASK_ADMIN_DESTINATION,
} from "@/lib/transformations/dbtSettings";
import { gitRefTerm } from "@/lib/transformations/repoFileUrl";
import { RepositoryName, RepositoryWithheld } from "./RepositoryName";

/**
 * What the next run will clone and run, read-only (canvas 16, Bundle tab).
 *
 * From `graph.bundle` (the binding as it is now), `graph.project` (the last
 * manifest summary) and `graph.run` (the merged selectors the last step ran
 * with). Changed on the Destination screen only, which the card links to for a reader who can
 * open it (`instance.binding.read`, #1106).
 *
 * The Target row is `bundle.target`: where dbt builds, names only (database,
 * schema, warehouse). The API never sends the account, the user, the role (ADR
 * 0011) or a secret reference, so the card cannot show one.
 */

function targetLine(target: TransformationGraph["bundle"]["target"]): string | null {
  if (!target) return null;
  if (target.destinationType === "databricks") {
    return target.warehouse ? `Databricks · SQL warehouse ${target.warehouse}` : "Databricks";
  }
  const relation = [target.database, target.schema].filter(Boolean).join(".");
  const parts = ["Snowflake", relation || "no database or schema set"];
  if (target.warehouse) parts.push(`warehouse ${target.warehouse}`);
  return parts.join(" · ");
}

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <>
      <dt>
        <SkyText size={200} color="muted">
          {term}
        </SkyText>
      </dt>
      <dd>{children}</dd>
    </>
  );
}

export function BundleFacts({ graph }: { graph: TransformationGraph }) {
  const [, navigate] = useLocation();
  // #1106: the dbt repository tab is Instance admin and above; anyone else gets no button to it.
  const canOpenDestination = usePermissions().canAny("instance.binding.read");
  const { bundle, project, run } = graph;
  const target = targetLine(bundle.target);
  return (
    <SkyCard
      appearance="outline"
      testId="bundle-facts"
      header={{
        title: "Bundle",
        action: (
          <span className="flex items-center gap-2">
            <SkyBadge appearance="tint" color="subtle">
              Read-only
            </SkyBadge>
            {canOpenDestination ? (
              <SkyButton
                appearance="subtle"
                size="small"
                // Round 6 (T2): the bundle's repository is on the dbt repository tab.
                onClick={() => navigate(absolutePath(DBT_SETTINGS_PATH))}
                testId="bundle-open-destination"
              >
                {OPEN_DBT_SETTINGS_LABEL}
              </SkyButton>
            ) : null}
          </span>
        ),
      }}
    >
      <dl className="grid grid-cols-[minmax(9rem,auto)_1fr] gap-x-4 gap-y-2">
        <Fact term="Repository">
          {bundle.repoBound && bundle.repoUrl ? (
            <RepositoryName repoUrl={bundle.repoUrl} />
          ) : bundle.repoBound && bundle.repoUrlWithheld ? (
            <RepositoryWithheld testId="bundle-repository-withheld" />
          ) : (
            <SkyText size={200} color="muted">
              Not set
            </SkyText>
          )}
        </Fact>
        {/* Round 7 T-38: Branch, not Ref (a 40-hex commit reads "Tag or commit"). */}
        <Fact term={bundle.repoBound && bundle.ref ? gitRefTerm(bundle.ref) : "Branch"}>
          {bundle.repoBound && bundle.detailsWithheld ? (
            <SkyText size={200} color="muted" block testId="bundle-branch-withheld">
              {BUNDLE_DETAIL_WITHHELD}
            </SkyText>
          ) : (
            <SkyText size={200} block>
              {bundle.repoBound ? bundle.ref : "—"}
            </SkyText>
          )}
        </Fact>
        <Fact term="Project subdirectory">
          {bundle.repoBound && bundle.detailsWithheld ? (
            <SkyText size={200} color="muted" block testId="bundle-subdir-withheld">
              {BUNDLE_DETAIL_WITHHELD}
            </SkyText>
          ) : (
            <SkyText size={200} block>
              {bundle.repoBound ? (bundle.subdir ?? "/ (repository root)") : "—"}
            </SkyText>
          )}
        </Fact>
        <Fact term="Target">
          {target && bundle.detailsWithheld && bundle.target?.accountBound === false ? (
            <SkyText size={200} color="muted" testId="bundle-target">
              No destination set
            </SkyText>
          ) : target ? (
            <>
              <SkyText
                size={200}
                font="monospace"
                block
                className="break-all"
                testId="bundle-target"
              >
                {target}
              </SkyText>
              {/* #1362: the warehouse is Instance admin and above, the database and schema are
                  every reader's, as on Your data. */}
              {bundle.detailsWithheld ? (
                <SkyText size={200} color="muted" block testId="bundle-target-withheld">
                  Warehouse: {ASK_ADMIN_DESTINATION}
                </SkyText>
              ) : null}
            </>
          ) : (
            <SkyText size={200} color="muted" testId="bundle-target">
              No destination set
            </SkyText>
          )}
        </Fact>
        {/* #1207 with #1191: the dbt version a person needs, never the image tag. */}
        <Fact term="dbt version">
          <SkyText size={200} block>
            {project?.dbtVersion ?? run?.dbtVersion ?? "Not recorded"}
          </SkyText>
        </Fact>
        <Fact term="Project">
          <SkyText size={200} block>
            {project ? `${project.name ?? "—"} · ${project.nodeCount} nodes in the project` : "—"}
          </SkyText>
        </Fact>
        <Fact term="Contract">
          <SkyText size={200} block>
            summary dbt-summary/1.2.0 · manifest dbt-manifest-summary/1.3.0 · source
            dbt-node-source/1.0.0
          </SkyText>
        </Fact>
        <Fact term="Selection">
          {run?.select ? (
            <SkyText size={200} font="monospace" block className="break-all">
              {run.select}
              {run.testSelect ? ` · tests: ${run.testSelect}` : " · tests off"}
            </SkyText>
          ) : (
            <SkyText size={200} color="muted">
              No run yet
            </SkyText>
          )}
        </Fact>
      </dl>
    </SkyCard>
  );
}
